import { FC, HTMLAttributes, useEffect, useMemo, useState } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { FiMapPin } from 'react-icons/fi';
import { SubmissionErrorModal } from '../../../components/errors/SubmissionErrorModal';
import { Button } from '../../../components/shared/button/Button';
import { Watch } from '../../../components/shared/icons/Watch';
import { InfoChit, InfoChitImportance } from '../../../components/shared/InfoChit';
import { constructNameEN } from '../../../employee/employee-name-helper';
import { dateToGQLFormat } from '../../../helpers/dateHelper';
import { handleMutationResult } from '../../../helpers/toastHelper';
import { isManagement, useAuthenticationService } from '../../../services/authentication/authentication-service';
import { Feature } from '../../../services/backstage/Feature';
import { ClockInStatus } from '../../../services/clockIn/ClockService';
import {
    GetMyOpenWorkRecordsQuery,
    GetOpenWorkRecordsQuery,
    ReadEmployee,
    useEndMyWorkTimeRecordMutation,
    useEndWorkTimeRecordMutation,
    useGetMyOpenWorkRecordsQuery,
    useGetOpenWorkRecordsQuery,
    useStartMyWorkTimeRecordMutation,
    useStartWorkTimeRecordMutation,
    WorkTimeDataSource
} from '../../../services/gql/graphql.generated';
import { useLocationService } from '../../../services/location/LocationService';
import { createSuccessToast } from '../../../services/notification/ToastService';
import { EmployeeAvatar } from '../../home/clockIn/EmployeeAvatar';
import { EmployeeStatus } from '../../home/clockIn/EmployeePresenceCard';
import { UpdateSelectedLocationsModal } from '../../shifts/UpdateSelectedLocationsModal';
import strings from '../team.translations';
import { TeamMemberClockModal } from './TeamMemberClockModal';
import { TeamMemberUpdateType, useTeamMemberService } from './TeamMemberService';

interface TeamMemberHeaderContentProps extends HTMLAttributes<HTMLDivElement> {
  employee: ReadEmployee;
  sendInfoCallback?: () => void;
  isMe?: boolean;
  selectedTab?: string;
}

export const TeamMemberHeaderContent: FC<TeamMemberHeaderContentProps> = ({
  selectedTab,
  employee,
  sendInfoCallback,
  isMe
}) => {
  const [clockError, setClockError] = useState<Error>();
  const [errorModalOpen, setErrorModalOpen] = useState<boolean>(false);
  const [clockModalOpen, setClockModalOpen] = useState<boolean>(false);

  const locationList = useLocationService(state => state.locationList);
  const allowedLocations = locationList.filter(loc => {
    return !!employee.locations?.find(tmLoc => tmLoc.id === loc.id);
  });

  const statusUpdateNeeded = useTeamMemberService(state => state.updateNeeded === TeamMemberUpdateType.HEADER);
  const updateComplete = useTeamMemberService(state => state.updateComplete);
  const forceUpdate = useTeamMemberService(state => state.forceUpdate);

  const handleError = useErrorHandler();
  const queryEndpoint = isMe ? useGetMyOpenWorkRecordsQuery : useGetOpenWorkRecordsQuery;
  const [{ data, error, fetching }, refetchOpenRecords] = queryEndpoint({
    variables: {
      args: {
        employeeId: isMe ? undefined : employee.identityId
      }
    },
    context: useMemo(
      () => ({
        suspense: false
      }),
      []
    ),
    requestPolicy: 'network-only'
  });

  if (error) {
    handleError(error);
  }

  useEffect(() => {
    if (statusUpdateNeeded) {
      refetchOpenRecords();
      updateComplete(TeamMemberUpdateType.HEADER);
    }
  }, [updateComplete, statusUpdateNeeded, refetchOpenRecords]);

  let onShift: boolean | undefined;
  let clockLocationId: number | undefined;

  if (data) {
    if (isMe) {
      const dataToRead = data as GetMyOpenWorkRecordsQuery;
      onShift = dataToRead.myOpenWorkTimeRecord && dataToRead.myOpenWorkTimeRecord.length > 0;
      if (onShift) {
        clockLocationId = dataToRead.myOpenWorkTimeRecord![0].locationId;
      }
    } else {
      const dataToRead = data as GetOpenWorkRecordsQuery;
      onShift = dataToRead.openWorkTimeRecord && dataToRead.openWorkTimeRecord.length > 0;
      if (onShift) {
        clockLocationId = dataToRead.openWorkTimeRecord![0].locationId;
      }
    }
  }

  const startMutation = isMe ? useStartMyWorkTimeRecordMutation : useStartWorkTimeRecordMutation;
  const [startStatus, startRecord] = startMutation();
  const endMutation = isMe ? useEndMyWorkTimeRecordMutation : useEndWorkTimeRecordMutation;
  const [endStatus, endRecord] = endMutation();
  const clockLoading = startStatus.fetching || endStatus.fetching;

  const handleClock = async () => {
    if (allowedLocations.length > 1) {
      setClockModalOpen(true);
    } else {
      const locationId = allowedLocations[0].id;
      const errorMessage = await submitClock(locationId);

      if (!errorMessage) {
        createSuccessToast(
          onShift
            ? strings.clock.clockedOut.message(employee.firstName)
            : strings.clock.clockedIn.message(employee.firstName)
        );
      } else {
        setClockError(new Error(errorMessage));
        setErrorModalOpen(true);
      }
    }
  };

  const submitClock = async (locationId: number) => {
    const result = onShift
      ? await endRecord({
          workTimeRecordEnd: {
            employeeId: !isMe ? employee.identityId : undefined,
            locationId: locationId,
            end: dateToGQLFormat(new Date()),
            endSource: WorkTimeDataSource.Override
          }
        })
      : await startRecord({
          workTimeRecordStart: {
            employeeId: !isMe ? employee.identityId : undefined,
            locationId: locationId,
            start: dateToGQLFormat(new Date()),
            startSource: WorkTimeDataSource.Override
          }
        });

    const success = await handleMutationResult(result, {
      errorMessage: strings.common.toastNotifications.error,
      onSuccess: onClockComplete
    });

    return success ? undefined : result.error?.message || strings.common.toastNotifications.error;
  };

  const onClockComplete = () => {
    refetchOpenRecords({ requestPolicy: 'network-only' });
    forceUpdate(TeamMemberUpdateType.SHIFTS);
  };

  const role = useAuthenticationService(state => state.role);
  const isManager = isManagement(role);
  const deletedOrDeactivatedAccountString = employee.isDeleted
    ? strings.deletedAccount
    : employee.isDeactivated
      ? strings.deactivatedAccount
      : '';

  const memberName = constructNameEN(employee.firstName ?? '', employee.lastName ?? '');

  const locations = useLocationService(state => state.locationList);

  const [showUpdateLocationModal, setShowUpdateLocationModal] = useState<boolean>(false);

  return (
    <>
      <div className={'flex flex-col md:flex-row justify-between lg:relative'}>
        <div className="flex flex-row gap-8 items-center text-4xl font-bold">
          {!employee.isDeleted && (
            <EmployeeAvatar
              employee={{
                firstName: employee.firstName ?? '',
                lastName: employee.lastName ?? '',
                avatarUrl: employee.avatarUrl ?? undefined,
                status: onShift ? EmployeeStatus.ON_SHIFT : EmployeeStatus.NOT_ON_SHIFT,
                id: ''
              }}
              fetching={fetching}
            />
          )}
          <div>
            <p>{isMe && !isManager ? strings.myProfileTitle : memberName}</p>
            {deletedOrDeactivatedAccountString && (
              <InfoChit importance={InfoChitImportance.HIGH}>
                <p className="p-2 leading-4 mt-0.5 text-sm">{deletedOrDeactivatedAccountString}</p>
              </InfoChit>
            )}
          </div>
        </div>
        <Feature name="clockIn">
          <div className="lg:absolute bottom-0 right-0">
            <div className="flex flex-col md:flex-row gap-2 md:gap-5 ">
              {selectedTab === strings.verticalTabMenu.shiftsLabel && locations?.length > 1 && (
                <Button
                  onClick={() => setShowUpdateLocationModal(true)}
                  primary
                  className="flex items-center justify-center w-full gap-2 md:w-max self-center mt-2 md:mt-0"
                >
                  <FiMapPin className="w-4 h-6" />
                  <span>{strings.selectLoc}</span>
                </Button>
              )}
              {isManager && !deletedOrDeactivatedAccountString && (
                <Button
                  loading={fetching || clockLoading}
                  onClick={handleClock}
                  primary
                  className="flex items-center justify-center w-full gap-2 md:w-max self-center mt-2 md:mt-0"
                >
                  <Watch className="w-4 h-6" />
                  {onShift
                    ? strings.clock.clockPersonOut(employee.firstName)
                    : strings.clock.clockPersonIn(employee.firstName)}
                </Button>
              )}
            </div>
          </div>
        </Feature>

        <TeamMemberClockModal
          open={clockModalOpen}
          onClose={() => setClockModalOpen(false)}
          onSubmit={submitClock}
          onSuccess={() =>
            createSuccessToast(
              onShift
                ? strings.clock.clockedOut.message(employee.firstName)
                : strings.clock.clockedIn.message(employee.firstName)
            )
          }
          loading={clockLoading}
          clockState={onShift ? ClockInStatus.In : ClockInStatus.Out}
          alreadyClockedLocation={clockLocationId}
          locationList={allowedLocations}
        />
        <UpdateSelectedLocationsModal
          onCompleteCallback={() => {
            setShowUpdateLocationModal(false);
          }}
          open={showUpdateLocationModal}
          onClose={() => setShowUpdateLocationModal(false)}
        />
        <SubmissionErrorModal error={clockError!} onClose={() => setErrorModalOpen(false)} open={errorModalOpen} />
      </div>
    </>
  );
};
